import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode
} from "react";

interface RouteHandlerContextType {
  isRouteChecked: boolean;
}

const RouteHandlerContext = createContext<RouteHandlerContextType | undefined>(undefined);

export const useRouteHandler = (): RouteHandlerContextType => {
  const context = useContext(RouteHandlerContext);
  if (!context) {
    throw new Error("useRouteHandler must be used within RouteHandlerProvider");
  }
  return context;
};

interface RouteHandlerProviderProps {
  children: ReactNode;
}

export const RouteHandlerProvider = ({ children }: RouteHandlerProviderProps) => {
  const [isRouteChecked, setIsRouteChecked] = useState(false);

  const staticRoutes: readonly string[] = [
    "/new-h",
    "/new-h/quiz",
    "/new-h/plan",
    "/new-h/payment",
    "/new-h/offer",
    // TODO: Signup infrastructure
    // "/new-h/signup",
    "/new-h/thankyou",
    "/iq-test",
    "/iq-test/quiz",
    "/iq-test/plan",
    "/iq-test/payment",
    "/iq-test/offer",
    "/iq-test/thankyou",
    "/hp",
    "/hp/quiz",
    "/hp/waitlist",
    "/hp/reserve",
    "/hp/reserve-essential",
    "/hp/payment",
    "/hp/offer",
    "/hp/thankyou",
    "/hp-3",
    "/hp-3/quiz",
    "/hp-3/waitlist",
    "/hp-3/reserve",
    "/hp-3/reserve-essential",
    "/hp-3/payment",
    "/hp-3/thankyou",
    "/hp-23d",
    "/hp-23d/quiz",
    "/hp-23d/waitlist",
    "/hp-23d/reserve",
    "/hp-23d/reserve-essential",
    "/hp-23d/payment",
    "/hp-23d/thankyou",
    "/new-h2",
    "/new-h2/quiz",
    "/new-h2/plan",
    "/new-h2/payment",
    "/new-h2/offer",
    "/new-h2/signup",
    "/new-h2/thankyou",
    "/new-s",
    "/new-s/quiz",
    "/new-s/plan",
    "/new-s/payment",
    "/new-s/offer",
    "/new-s/signup",
    "/new-s/thankyou",
    "/new-t",
    "/new-t/quiz",
    "/new-t/plan",
    "/new-t/payment",
    "/new-t/offer",
    "/new-t/thankyou",
    "/new-t2",
    "/new-t2/quiz",
    "/new-t2/plan",
    "/new-t2/payment",
    "/new-t2/offer",
    "/new-t2/thankyou",
    "/apply-discount",
    "/apply-discount/payment",
    "/apply-discount/offer",
    "/apply-discount/thankyou",
    "/learning-desk-1",
    "/w2a",
    "/w2a/reading",
    "/w2a/lessons",
    "/w2a/mind-palace",
    "/checkout",
    "/checkout/thankyou",
    "/dev/test/test_component",
    "/.well-known/apple-developer-merchantid-domain-association.txt",
    "/apple-developer-merchantid-domain-association.txt"
  ] as const;

  const routePatterns: readonly RegExp[] = [
    /^\/(?:new-t|new-t2|new-h|new-h2|new-s|hp|hp-3|hp-23d|checkout|iq-test)\/quiz\/\d+$/,
    /^\/(?:new-t|new-t2|new-h|new-h2|new-s|hp|hp-3|hp-23d|checkout|iq-test)\/quiz\/\d+\/\d+$/,
  ] as const;

  const isValidRoute = (path: string): boolean => {
    if (staticRoutes.includes(path)) {
      return true;
    }
    return routePatterns.some(pattern => pattern.test(path));
  };

  const cleanPath = (path: string): string => {
    let cleanedPath = path.replace(/\/+/g, '/');

    if (cleanedPath.length > 1 && cleanedPath.endsWith('/')) {
      cleanedPath = cleanedPath.slice(0, -1);
    }

    const cleanedPathParts = cleanedPath.split('/').map(part => {
      if (!part) return part;
      return part.replace(/[^a-zA-Z0-9-_.~:]/g, '');
    });

    return cleanedPathParts.join('/');
  };

  const extractAllParams = (url: string): Record<string, string> => {
    const searchParams = new URLSearchParams(url.split('?')[1] || '');
    const allParams: Record<string, string> = {};

    searchParams.forEach((value, key) => {
      allParams[key] = value;
    });

    return allParams;
  };

  const buildRedirectUrl = (targetPath: string, params: Record<string, string>): string => {
    const redirectUrl = new URL(targetPath, window.location.origin);

    Object.entries(params).forEach(([key, value]) => {
      redirectUrl.searchParams.append(key, value);
    });

    return redirectUrl.toString();
  };

  useEffect(() => {
    const originalPath = window.location.pathname;
    const search = window.location.search;
    const hash = window.location.hash;
    const fullPath = originalPath + search + hash;

    const cleanedPath = cleanPath(originalPath);
    const needsCleaning = cleanedPath !== originalPath;

    const isPathValid = isValidRoute(cleanedPath);

    const allParams = extractAllParams(fullPath);

    if (needsCleaning && isValidRoute(cleanedPath)) {
      const newUrl = cleanedPath + search + hash;
      window.history.replaceState(null, '', newUrl);
      setIsRouteChecked(true);
    } else if (!isPathValid) {
      const redirectUrl = buildRedirectUrl('/new-h2', allParams);
      if (window.location.href !== redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        setIsRouteChecked(true);
      }
    } else {
      setIsRouteChecked(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue: RouteHandlerContextType = {
    isRouteChecked,
  };

  return (
    <RouteHandlerContext.Provider value={contextValue}>
      {isRouteChecked ? children : null}
    </RouteHandlerContext.Provider>
  );
};
